import React, { useState } from 'react';
import { getAuthHeaders } from '../supabaseClient';

function AccountSetup({ session, onComplete, onSignInDifferent }) {
    const [mode, setMode] = useState(null);
    const [accountName, setAccountName] = useState('');
    const [inviteCode, setInviteCode] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [corpusOptIn, setCorpusOptIn] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleCreate = async () => {
        if (!accountName.trim()) return setError('Please enter an account name.');
        setLoading(true);
        setError('');
        try {
            const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/accounts/create', {
                method: 'POST',
                headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ account_name: accountName.trim(), display_name: displayName.trim(), corpus_opt_in: corpusOptIn }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            onComplete(data);
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    };

    const handleJoin = async () => {
        if (!inviteCode.trim()) return setError('Please enter an invite code.');
        setLoading(true);
        setError('');
        try {
            const res = await fetch('https://tm-audit-platform-production.up.railway.app/api/accounts/join', {
                method: 'POST',
                headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ invite_code: inviteCode.trim(), display_name: displayName.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            onComplete(data);
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    };

    return (
        <div className="account-setup-screen">
            <div className="account-setup-inner">
                <div className="logo-lockup" style={{ justifyContent: 'center', marginBottom: 8 }}>
                    <span className="logo-audee">Audee<span className="logo-dot">.</span></span>
                </div>
                <p className="account-setup-sub">Set up your account to get started.</p>

                {!mode && (
                    <div className="account-setup-choices">
                        <button className="account-choice-btn" onClick={() => setMode('create')}>
                            <div className="account-choice-title">Create an Account</div>
                            <div className="account-choice-desc">Start a new account for your company</div>
                        </button>
                        <button className="account-choice-btn" onClick={() => setMode('join')}>
                            <div className="account-choice-title">Join an Account</div>
                            <div className="account-choice-desc">Use an invite code from your team admin</div>
                        </button>
                    </div>
                )}

                {/* This screen only renders for a signed-in user with no profile, so an
                    existing customer who lands here is signed in as the wrong login.
                    Name the login and offer the existing sign-in flow (sign out → LoginScreen). */}
                {!mode && onSignInDifferent && (
                    <p className="account-setup-signin">
                        Signed in as {session?.user?.email}.{' '}
                        Already have an account?{' '}
                        <button className="account-setup-signin-link" onClick={onSignInDifferent}>Sign in</button>
                    </p>
                )}

                {mode && (
                    <div className="account-setup-form">
                        <div className="login-field">
                            <label>Your Name</label>
                            <input
                                type="text"
                                placeholder="e.g. Jane Smith"
                                value={displayName}
                                onChange={e => setDisplayName(e.target.value)}
                            />
                        </div>

                        {mode === 'create' && (
                            <div className="login-field">
                                <label>Account Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Acme Restoration"
                                    value={accountName}
                                    onChange={e => setAccountName(e.target.value)}
                                />
                            </div>
                        )}

                        {mode === 'join' && (
                            <div className="login-field">
                                <label>Invite Code</label>
                                <input
                                    type="text"
                                    placeholder="Enter 6-digit code"
                                    value={inviteCode}
                                    onChange={e => setInviteCode(e.target.value.toUpperCase())}
                                    maxLength={6}
                                    style={{ textTransform: 'uppercase', letterSpacing: '4px' }}
                                />
                            </div>
                        )}

                        {/* Product-improvement disclosure (always shown; NOT a toggle).
                            Distinct from the optional corpus opt-in below: this covers
                            internal product-improvement learning, which is decoupled from
                            corpus consent. */}
                        <div className="login-field" style={{ padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)' }}>
                            <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
                                <strong style={{ color: 'var(--text)' }}>Product improvement.</strong>{' '}
                                As a beta design partner, you agree that Audee may analyze the estimates and audits
                                you run in order to improve the accuracy and performance of the Audee product. This
                                internal use never shares your data outside Audee. Separately, contributing your data
                                to Audee's anonymized training corpus is strictly optional and off by default — we only
                                do so if you explicitly opt in.
                            </span>
                            {/* Retention/manual-review disclosure (GAP-076). The failed-audit
                                messages in backend/audit-failure-log.js offer the customer a human
                                reviewer, and 0032_audit_failure_file_retention.sql keeps the file so
                                that offer is keepable. This is the notice for that retention. Copy
                                only — it gates nothing and does not touch the corpus opt-in below,
                                which remains the sole basis for corpus contribution. */}
                            <span style={{ display: 'block', marginTop: 10, fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
                                To provide and improve the service, Audee may securely retain the documents you
                                upload — including uploads we could not process — and a member of our team may
                                review them. Retained failed-upload files are deleted after 30 days. This is
                                separate from the optional anonymized training corpus below.
                            </span>
                        </div>

                        {/* Corpus Improvement Program consent (account-level; set by the account creator). */}
                        {mode === 'create' && (
                            <label className="login-field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={corpusOptIn}
                                    onChange={e => setCorpusOptIn(e.target.checked)}
                                    style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0 }}
                                />
                                <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
                                    <strong style={{ color: 'var(--text)' }}>Help make Audee sharper — including your own audits.</strong>{' '}
                                    Turn this on and your uploaded estimates help Audee catch more errors, read documents
                                    more accurately, and benchmark against the wider field — improvements that flow right
                                    back into the audits you run. Your estimates are anonymized first: names, addresses,
                                    emails, phone numbers, claim identifiers, and policy numbers are removed before
                                    anything enters the internal corpus. You can change this anytime in Account settings.
                                </span>
                            </label>
                        )}

                        {error && <div className="login-error">{error}</div>}

                        <button
                            className="login-btn"
                            onClick={mode === 'create' ? handleCreate : handleJoin}
                            disabled={loading}
                        >
                            {loading ? 'Please wait...' : mode === 'create' ? 'Create Account' : 'Join Account'}
                        </button>

                        <button className="history-btn" onClick={() => { setMode(null); setError(''); }}>
                            ← Back
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AccountSetup;
